import { sampleStandardGamepadDrive, sampleStandardGamepadEdges } from "./gamepad";
import { mapScreenSteerToTrack } from "./steering";

export type DriveInput = {
  throttle: number;
  brake: number;
  steer: number;
  drift: boolean;
  boost: boolean;
};

export type InputEvents = {
  start: boolean;
  camera: boolean;
  pause: boolean;
  recover: boolean;
};

const KEY_ACTIONS = new Map<string, string>([
  ["ArrowUp", "throttle"],
  ["KeyW", "throttle"],
  ["ArrowDown", "brake"],
  ["KeyS", "brake"],
  ["ArrowLeft", "left"],
  ["KeyA", "left"],
  ["ArrowRight", "right"],
  ["KeyD", "right"],
  ["Space", "drift"],
  ["ShiftLeft", "boost"],
  ["ShiftRight", "boost"]
]);

const EDGE_ACTIONS = new Map<string, keyof InputEvents>([
  ["KeyC", "camera"],
  ["Escape", "pause"],
  ["KeyP", "pause"],
  ["KeyR", "recover"]
]);

export class InputController {
  readonly #pressed = new Set<string>();
  readonly #pointerActions = new Map<number, string>();
  readonly #events: InputEvents = { start: false, camera: false, pause: false, recover: false };
  readonly #abortController = new AbortController();
  #previousGamepadButtons: boolean[] = [];

  constructor(touchRoot: HTMLElement) {
    const options = { signal: this.#abortController.signal };
    window.addEventListener("keydown", this.#onKeyDown, options);
    window.addEventListener("keyup", this.#onKeyUp, options);
    window.addEventListener("blur", this.#clear, options);
    touchRoot.addEventListener("pointerdown", this.#onPointerDown, options);
    touchRoot.addEventListener("pointerup", this.#onPointerUp, options);
    touchRoot.addEventListener("pointercancel", this.#onPointerUp, options);
    touchRoot.addEventListener("click", this.#onTouchEvent, options);
    touchRoot.addEventListener("contextmenu", this.#preventContextMenu, options);
  }

  sample(): DriveInput {
    const gamepad = navigator.getGamepads().find((candidate) => candidate !== null);
    const gamepadDrive = sampleStandardGamepadDrive(gamepad);
    const screenSteer = Number(this.#isActive("right")) - Number(this.#isActive("left"));
    const keyboardSteer = mapScreenSteerToTrack(screenSteer);
    this.#sampleGamepadEdges(gamepad);

    return {
      throttle: Math.max(Number(this.#isActive("throttle")), gamepadDrive.throttle),
      brake: Math.max(Number(this.#isActive("brake")), gamepadDrive.brake),
      steer: Math.abs(gamepadDrive.steer) > 0.12 ? gamepadDrive.steer : keyboardSteer,
      drift: this.#isActive("drift") || gamepadDrive.drift,
      boost: this.#isActive("boost") || gamepadDrive.boost
    };
  }

  consumeEvents(): InputEvents {
    const events = { ...this.#events };
    this.#events.start = false;
    this.#events.camera = false;
    this.#events.pause = false;
    this.#events.recover = false;
    return events;
  }

  dispose(): void {
    this.#abortController.abort();
    this.#clear();
  }

  #isActive(action: string): boolean {
    return this.#pressed.has(action) || [...this.#pointerActions.values()].includes(action);
  }

  #sampleGamepadEdges(gamepad: Gamepad | null | undefined): void {
    if (!gamepad) {
      this.#previousGamepadButtons = [];
      return;
    }

    const current = gamepad.buttons.map((button) => button.pressed);
    const edges = sampleStandardGamepadEdges(current, this.#previousGamepadButtons);
    this.#events.start ||= edges.start;
    this.#events.camera ||= edges.camera;
    this.#events.pause ||= edges.pause;
    this.#events.recover ||= edges.recover;
    this.#previousGamepadButtons = current;
  }

  readonly #onKeyDown = (event: KeyboardEvent): void => {
    const action = KEY_ACTIONS.get(event.code);
    if (action) {
      this.#pressed.add(action);
      event.preventDefault();
    }

    const edgeAction = EDGE_ACTIONS.get(event.code);
    if (edgeAction && !event.repeat) {
      this.#events[edgeAction] = true;
      event.preventDefault();
    }
  };

  readonly #onKeyUp = (event: KeyboardEvent): void => {
    const action = KEY_ACTIONS.get(event.code);
    if (action) {
      this.#pressed.delete(action);
      event.preventDefault();
    }
  };

  readonly #onPointerDown = (event: PointerEvent): void => {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-drive-action]") : null;
    const action = target?.dataset.driveAction;
    if (!target || !action) return;
    if (event.isTrusted) target.setPointerCapture(event.pointerId);
    this.#pointerActions.set(event.pointerId, action);
    target.dataset.active = "true";
    event.preventDefault();
  };

  readonly #onPointerUp = (event: PointerEvent): void => {
    const action = this.#pointerActions.get(event.pointerId);
    if (!action) return;
    this.#pointerActions.delete(event.pointerId);
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-drive-action]") : null;
    if (target) delete target.dataset.active;
    event.preventDefault();
  };

  readonly #onTouchEvent = (event: Event): void => {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-input-event]") : null;
    const inputEvent = target?.dataset.inputEvent;
    if (inputEvent === "pause" || inputEvent === "recover") {
      this.#events[inputEvent] = true;
      event.preventDefault();
    }
  };

  readonly #preventContextMenu = (event: Event): void => event.preventDefault();

  readonly #clear = (): void => {
    this.#pressed.clear();
    this.#pointerActions.clear();
    document.querySelectorAll<HTMLElement>("[data-drive-action][data-active]").forEach((element) => delete element.dataset.active);
  };
}
